import { useCallback, useLayoutEffect, useRef } from 'react';
import dayJs from 'dayjs';
import timezonePlugin from 'dayjs/plugin/timezone';
import utcPlugin from 'dayjs/plugin/utc';
import { cellDuration } from 'pos-client/components/scheduleBoard/constants/boardConfig.ts';
import type { EventHandler } from 'pos-client/components/scheduleBoard/types/boardTypes.ts';
import type {
  ScheduleBoardBlockedSlotModel,
  ScheduleBoardJobsAndBlockedSlotsModel,
} from 'pos-shared/models/schedulingSchema';

dayJs.extend(utcPlugin);
dayJs.extend(timezonePlugin);

export const useEvent = (handler: EventHandler) => {
  const handlerRef = useRef<EventHandler | null>(null);

  useLayoutEffect(() => {
    handlerRef.current = handler;
  });

  return useCallback((...args: Parameters<EventHandler>) => {
    const fn = handlerRef.current as unknown;
    return typeof fn === 'function' && fn(...args);
  }, []);
};

export const getEstimatedTime = (time: string | Date | undefined) => {
  let estimatedTime = '';

  if (time instanceof Date) {
    estimatedTime = time.toISOString();
  } else if (time !== undefined) {
    estimatedTime = time;
  }

  // Remove timezone offset (e.g., -0800 or +0530) if present
  return estimatedTime.replace(/([+-]\d{2}:?\d{2}|Z)$/, '');
};

export const getEstimatedTimeInLocationTimeZone = (
  time: string | Date | undefined,
  locationTimeZone: string | null | undefined
) => {
  const timeWithoutTimezone = getEstimatedTime(time);
  if (!timeWithoutTimezone || !locationTimeZone) return timeWithoutTimezone;
  return dayJs.tz(timeWithoutTimezone, locationTimeZone).format();
};

export const getDuration = (startDate: string, endDate: string) => {
  return Number(new Date(endDate)) - Number(new Date(startDate));
};

export const navigateTo = (url: string) => {
  window.open(url, '_blank');
};

export const getTime = (dateTimeString: string) => {
  return dateTimeString.split('T')[1].replace(/:00$/, '');
};

export const cellTimeIsTradingTime = (
  cellDateString: string,
  tradingTime: string | undefined
) => {
  const cellTimeOnly = dayJs(cellDateString).format('HH:mm:ss');

  return cellTimeOnly === tradingTime;
};

export const tradingTimeStringToMinutes = (
  time: string | undefined
): number | undefined => {
  if (!time) return undefined;
  const [hoursPart, minutesPart] = time.split(':');
  const hours = Number.parseInt(hoursPart, 10);
  const minutes = Number.parseInt(minutesPart, 10);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return undefined;
  return hours * 60 + minutes;
};

export const cellIsBeforeOpening = (
  cellDateString: string,
  tradingTime: string | undefined
) => {
  const cellTimeOnly = dayJs(cellDateString).format('HH:mm:ss');

  return tradingTime && cellTimeOnly < tradingTime;
};

export const cellIsAfterClosing = (
  cellDateString: string,
  tradingTime: string | undefined
) => {
  const cellTimeOnly = dayJs(cellDateString).format('HH:mm:ss');

  return tradingTime && cellTimeOnly >= tradingTime;
};

export const roundMinutesInTimeStringToCellDuration = (
  timeString: string | undefined,
  roundUpOrDown: 'roundUp' | 'roundDown'
) => {
  if (!timeString) return undefined;

  const hours = parseInt(timeString.split(':')[0], 10);
  const minutes = parseInt(timeString.split(':')[1], 10);

  const totalMinutes = hours * 60 + minutes;

  const roundedMinutes =
    roundUpOrDown === 'roundUp'
      ? Math.ceil(totalMinutes / cellDuration) * cellDuration
      : Math.floor(totalMinutes / cellDuration) * cellDuration;

  const roundedHours = Math.floor(roundedMinutes / 60);
  const roundedMinutesRemainder = roundedMinutes % 60;

  const roundedTimeString = `${String(roundedHours).padStart(2, '0')}:${String(
    roundedMinutesRemainder
  ).padStart(2, '0')}:00`;

  return roundedTimeString;
};

export const slotIsNotBeforeCurrentTimeOrOutsideTradingTimes = (
  currentDate: Date,
  startDate: string,
  tradingTimesStart: string | undefined,
  tradingTimesEnd: string | undefined
) => {
  const roundedStart = roundMinutesInTimeStringToCellDuration(
    tradingTimesStart,
    'roundDown'
  );

  return (
    !dayJs(startDate).isBefore(currentDate) &&
    // prevent add new order in the 2 hour buffer before opening and after closing
    !cellIsBeforeOpening(startDate, roundedStart) &&
    !cellIsAfterClosing(startDate, tradingTimesEnd)
  );
};

export const formatLocalISOString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
};

export const isBlockedSlot = (
  slot: ScheduleBoardJobsAndBlockedSlotsModel
): slot is ScheduleBoardBlockedSlotModel => slot.text === 'blocked';
